import { z } from 'zod';
import {
  serverInspectionRequest,
  type ServerResources,
} from '@atum-remote/protocol/server-inspection';

export function bytes(value: number | undefined | null): string {
  if (value == null || !Number.isFinite(value)) return 'Unavailable';
  const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB'];
  const exponent = Math.min(4, Math.max(0, Math.floor(Math.log2(Math.max(1, value)) / 10)));
  return `${(value / 1024 ** exponent).toLocaleString(undefined, { maximumFractionDigits: 1 })} ${units[exponent]}`;
}
export function cpuUsage(
  before: ServerResources | undefined,
  after: ServerResources,
): number | null {
  if (
    !before?.cpu ||
    !after.cpu ||
    before.cpu.cores !== after.cpu.cores ||
    before.uptime == null ||
    after.uptime == null ||
    after.uptime <= before.uptime
  )
    return null;
  const total = after.cpu.total - before.cpu.total,
    idle = after.cpu.idle - before.cpu.idle;
  return total > 0 && idle >= 0 && idle <= total ? (100 * (total - idle)) / total : null;
}
export function rootStorage(output: string) {
  const match = output
    .trim()
    .split('\n')
    .at(-1)
    ?.match(/^\S+\s+(\d+)\s+(\d+)\s+(\d+)\s+\d+%\s+\/$/);
  if (!match) return null;
  const total = Number(match[1]),
    used = Number(match[2]),
    available = Number(match[3]);
  // Reserved blocks are not allocatable by the enrolled non-root account.
  return total > 0 && used <= total && used + available > 0
    ? { total, used, available, percent: (used / (used + available)) * 100 }
    : null;
}
export function completeLines(text: string, incomplete: boolean) {
  const lines = text.split('\n');
  if (incomplete || lines.at(-1) === '') lines.pop();
  return lines;
}
export function serviceRows(text: string, incomplete: boolean) {
  return completeLines(text, incomplete).flatMap((line) => {
    const match = line.trim().match(/^(\S+\.service)\s+(\S+)\s+(\S+)\s+(\S+)\s*(.*)$/);
    return match
      ? [
          {
            unit: match[1]!,
            logsAvailable: serverInspectionRequest.safeParse({
              section: 'logs',
              unit: match[1],
              minutes: 60,
              priority: 'all',
            }).success,
            load: match[2]!,
            active: match[3]!,
            sub: match[4]!,
            description: match[5]!,
          },
        ]
      : [];
  });
}
export function processRows(text: string, incomplete: boolean) {
  return completeLines(text, incomplete)
    .flatMap((line) => {
      const match = line.trim().match(/^(\d+)\s+(\S+)\s+([\d.]+)\s+([\d.]+)\s+(\d+)\s+(.+)$/);
      return match
        ? [
            {
              pid: match[1]!,
              user: match[2]!,
              cpu: Number(match[3]),
              memory: Number(match[4]),
              bytes: Number(match[5]) * 1024,
              name: match[6]!,
            },
          ]
        : [];
    })
    .slice(0, 50);
}
const journalRow = z.object({
  __REALTIME_TIMESTAMP: z.string().regex(/^\d{1,17}$/),
  MESSAGE: z.union([z.string(), z.array(z.number())]).optional(),
  _SYSTEMD_UNIT: z.string().optional(),
  SYSLOG_IDENTIFIER: z.string().optional(),
  PRIORITY: z
    .string()
    .regex(/^[0-7]$/)
    .optional(),
});
export function journalRows(text: string, incomplete: boolean) {
  let skipped = 0;
  const entries = completeLines(text, incomplete).flatMap((line) => {
    if (!line.trim()) return [];
    try {
      const entry = journalRow.parse(JSON.parse(line));
      const milliseconds = Number(entry.__REALTIME_TIMESTAMP) / 1000;
      const date = new Date(milliseconds);
      if (!Number.isFinite(date.getTime())) throw new Error('Invalid journal timestamp');
      return [
        {
          at: date.toISOString(),
          unit: entry._SYSTEMD_UNIT ?? entry.SYSLOG_IDENTIFIER ?? 'System',
          priority: Number(entry.PRIORITY ?? 6),
          message:
            typeof entry.MESSAGE === 'string'
              ? // Journal messages are untrusted text; discard terminal control bytes.
                // eslint-disable-next-line no-control-regex
                entry.MESSAGE.replace(/[\u0000-\u0008\u000b-\u001f\u007f]/g, '')
              : '[Binary message]',
        },
      ];
    } catch {
      skipped++;
      return [];
    }
  });
  return { entries, skipped };
}
export const journalLevels = [
  'Emergency',
  'Alert',
  'Critical',
  'Error',
  'Warning',
  'Notice',
  'Info',
  'Debug',
];
